import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  AppFormInput,
  AppFormSearchableSelect,
  AppFormTextarea,
} from '../../../components/common/appForms';
import Button from '../../../components/common/Button/Button';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../../components/common/DecoratedHeader/DecoratedHeader';
import Icon from '../../../components/common/Icon/Icon';
import { useMealTracking } from '../../../contexts/MealTrackingContext';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import type { SupplementEntry, SupplementFormValues } from '../../../types/mealTracking';
import pageStyles from '../../Checkout/CheckoutPage.module.css';
import fupStyles from '../../Fup/Fup.module.css';
import {
  SUPPLEMENT_FREQUENCY_OPTIONS,
  SUPPLEMENT_NAME_SUGGESTIONS,
  SUPPLEMENT_UNIT_OPTIONS,
} from '../mealTracking.constants';
import {
  createEmptySupplementForm,
  supplementToFormValues,
  validateSupplementForm,
} from '../mealTracking.utils';
import styles from './MealFormModal.module.css';

interface SupplementFormModalProps {
  open: boolean;
  supplementId?: string | null;
  defaultDate: string;
  onClose: () => void;
  onSaved: (entry: SupplementEntry, meta: { isCreate: boolean }) => void;
}

const NAME_SUGGESTIONS_LIST_ID = 'supplement-name-suggestions';

const SupplementFormModal: React.FC<SupplementFormModalProps> = ({
  open,
  supplementId = null,
  defaultDate,
  onClose,
  onSaved,
}) => {
  const reduceMotion = useReducedMotion();
  const { getSupplementById, createSupplement, updateSupplement } = useMealTracking();
  const existing = supplementId ? getSupplementById(supplementId) : undefined;
  const isEdit = Boolean(existing);

  const [values, setValues] = useState<SupplementFormValues>(() => createEmptySupplementForm(defaultDate));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setValues(existing ? supplementToFormValues(existing) : createEmptySupplementForm(defaultDate));
    setErrors({});
    setIsDirty(false);
    setShowDiscardConfirm(false);
  }, [defaultDate, existing, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const requestClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }

    onClose();
  };

  useEscapeToClose(requestClose, open && !showDiscardConfirm);

  const updateField = <K extends keyof SupplementFormValues>(field: K, value: SupplementFormValues[K]) => {
    setIsDirty(true);
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateSupplementForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (isEdit && supplementId) {
      const updated = updateSupplement(supplementId, values);
      if (updated) {
        onSaved(updated, { isCreate: false });
      }
      return;
    }

    const created = createSupplement(values);
    onSaved(created, { isCreate: true });
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className={styles.overlay}
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.22 }}
          >
            <motion.div
              className={styles.panel}
              role="dialog"
              aria-modal="true"
              aria-labelledby="supplement-form-modal-title"
              initial={reduceMotion ? { opacity: 0 } : { y: '100%' }}
              animate={reduceMotion ? { opacity: 1 } : { y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { y: '100%' }}
              transition={{ duration: reduceMotion ? 0.15 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            >
              <header className={styles.header}>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={requestClose}
                  aria-label="Fechar"
                >
                  <Icon name="close" size={22} />
                </button>
                <div className={styles.headerCopy}>
                  <DecoratedHeader
                    className={styles.title}
                    title={isEdit ? 'Editar suplemento' : 'Adicionar suplemento'}
                  />
                  <p id="supplement-form-modal-title" className={pageStyles.headerSubtitle}>
                    Informe o que você tomou, a quantidade e quando usou.
                  </p>
                </div>
              </header>

              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={pageStyles.formFields}>
                  <AppFormInput
                    id="supplement-name"
                    label="Nome do suplemento"
                    value={values.name}
                    list={NAME_SUGGESTIONS_LIST_ID}
                    placeholder="Ex.: Whey Protein"
                    onChange={(event) => updateField('name', event.target.value)}
                    error={errors.name}
                    required
                  />
                  <datalist id={NAME_SUGGESTIONS_LIST_ID}>
                    {SUPPLEMENT_NAME_SUGGESTIONS.map((suggestion) => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>

                  <div className={pageStyles.fieldRowHalf}>
                    <AppFormInput
                      id="supplement-amount"
                      label="Quantidade"
                      inputMode="decimal"
                      value={values.amount}
                      placeholder="Ex.: 30"
                      onChange={(event) => updateField('amount', event.target.value)}
                      error={errors.amount}
                      required
                    />
                    <AppFormSearchableSelect
                      id="supplement-unit"
                      label="Unidade"
                      required
                      value={values.unitId}
                      options={SUPPLEMENT_UNIT_OPTIONS}
                      placeholder="Selecione"
                      noResultsText="Nenhum resultado encontrado"
                      allowEmpty={false}
                      onChange={(event) => updateField(
                        'unitId',
                        event.target.value as SupplementFormValues['unitId'],
                      )}
                    />
                  </div>

                  {values.unitId === 'other' && (
                    <AppFormInput
                      id="supplement-custom-unit"
                      label="Unidade personalizada"
                      value={values.customUnit}
                      onChange={(event) => updateField('customUnit', event.target.value)}
                      error={errors.customUnit}
                      required
                    />
                  )}

                  <div className={pageStyles.fieldRowHalf}>
                    <AppFormInput
                      id="supplement-date"
                      label="Data"
                      type="date"
                      value={values.date}
                      onChange={(event) => updateField('date', event.target.value)}
                      error={errors.date}
                      required
                    />
                    <AppFormInput
                      id="supplement-time"
                      label="Horário"
                      type="time"
                      value={values.time}
                      onChange={(event) => updateField('time', event.target.value)}
                      error={errors.time}
                      required
                    />
                  </div>

                  <AppFormSearchableSelect
                    id="supplement-frequency"
                    label="Frequência"
                    required
                    value={values.frequencyId}
                    options={SUPPLEMENT_FREQUENCY_OPTIONS}
                    placeholder="Selecione"
                    noResultsText="Nenhum resultado encontrado"
                    allowEmpty={false}
                    onChange={(event) => updateField(
                      'frequencyId',
                      event.target.value as SupplementFormValues['frequencyId'],
                    )}
                  />

                  {values.frequencyId === 'custom' && (
                    <AppFormInput
                      id="supplement-custom-frequency"
                      label="Frequência personalizada"
                      value={values.customFrequency}
                      onChange={(event) => updateField('customFrequency', event.target.value)}
                      error={errors.customFrequency}
                      required
                    />
                  )}

                  <AppFormInput
                    id="supplement-brand"
                    label="Marca (opcional)"
                    value={values.brand}
                    onChange={(event) => updateField('brand', event.target.value)}
                  />

                  <AppFormTextarea
                    id="supplement-notes"
                    label="Observações (opcional)"
                    value={values.notes}
                    onChange={(event) => updateField('notes', event.target.value)}
                    placeholder="Detalhes extras, se quiser"
                    minRows={3}
                  />
                </div>

                <div className={fupStyles.footerActions}>
                  <div className={styles.actionsRow}>
                    <Button
                      type="button"
                      variant="outline"
                      size="app"
                      fullWidth
                      onClick={requestClose}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="app"
                      fullWidth
                      iconRight="arrow_forward"
                    >
                      Salvar suplemento
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        open={showDiscardConfirm}
        title="Descartar alterações"
        message="Você tem alterações não salvas. Deseja sair sem salvar?"
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        variant="danger"
        onCancel={() => setShowDiscardConfirm(false)}
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
      />
    </>
  );
};

export default SupplementFormModal;
